import type { Breadcrumb } from "@sentry/browser";
import * as Sentry from "@sentry/browser";
import React from "react";
import { hydrate, render } from "react-dom";

import { LoginSettingsInput } from "../../../__generated__/graphql";
import { RichTextDocument } from "../../../shared/block-editor-data/types";
import { ToastProvider } from "../../../shared/components/design-system/Toaster/context";
import { LanguageProvider } from "../../../shared/components/translation";
import ReportForm from "../../form";
import sendBeacon from "../../form/beacon";
import type { SiteEnvironment } from "../../site";
import ReporterMessaging from "../../your-case/App";
import "../css/globals.css";
import "../scss/index.scss";
import "./auth";
import CookieBanner from "./cookieBanner";
import CookieControl, { cookieAllowed, cookieBlocked, loadGoogleAnalytics } from "./cookieControl";
import initTitleObscurer from "./titleObscurer";
import wireUpFormLinks from "./wire-up-form-links";

declare global {
  interface Window {
    env: SiteEnvironment;
    cognitoSettings?: LoginSettingsInput;
    sla?: RichTextDocument;
  }
}

const uuids = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

function onContentLoad() {
  const language = document.querySelector("html")!.getAttribute("lang")!;

  // Navigation events are used by the form to control what page it's on,
  // so instead of firing this link, focus the page header with JS.
  // This does not need to run in no-JS mode since the no-JS is not affected by navigation events.
  document.getElementById("skip-to-content")?.addEventListener("click", (e) => {
    e.preventDefault();
    document.getElementById("page-heading")?.focus();
  });

  const cookieControlCheckbox = document.getElementById("cookie-control") as HTMLInputElement | null;
  if (cookieControlCheckbox) {
    new CookieControl(cookieControlCheckbox).init();
  }

  const universalAnalyticsBlock = document.getElementById("universal-analytics-block");
  const googleTagBlock = document.getElementById("google-tag-block");
  if (universalAnalyticsBlock || googleTagBlock) {
    if (cookieAllowed()) {
      loadGoogleAnalytics(!!universalAnalyticsBlock, googleTagBlock?.dataset.googleTagId);
    } else if (!cookieBlocked()) {
      new CookieBanner(document.getElementById("cookie-banner") as HTMLDivElement).init();
    }
  }

  document.getElementById("copy-code-button")?.addEventListener("click", (e) => {
    const copyText = document.getElementById("message-code") as HTMLInputElement;
    const toastPopover = document.getElementById("toaster-popout-copied") as HTMLInputElement;

    void navigator.clipboard.writeText(copyText?.value);
    toastPopover.classList.add("show");

    setTimeout(() => {
      toastPopover.classList.remove("show");
    }, 1000);
  });

  // You can also open and close the menu with the mouse:
  const openButton = document.getElementById("open-menu-button");
  const closeButton = document.getElementById("close-menu-button");
  const navMenu = document.getElementById("nav-menu");
  openButton?.addEventListener("click", (e) => {
    document.body.classList.add("is-mobile-nav");
    navMenu!.ariaHidden = "false";
    openButton!.ariaHidden = "false";
    closeButton!.ariaHidden = "false";
    closeButton?.focus();
  });
  closeButton?.addEventListener("click", (e) => {
    document.body.classList.remove("is-mobile-nav");
    navMenu!.ariaHidden = "true";
    closeButton!.ariaHidden = "true";
    openButton?.focus();
  });

  const reporterMessaging = document.getElementById("reporterMessaging");
  if (reporterMessaging) {
    const showCopyWarning = reporterMessaging?.getAttribute("data-warning") === "true";
    render(
      <LanguageProvider language={language}>
        <ToastProvider>
          <ReporterMessaging showCopyWarning={showCopyWarning} sla={window.sla} />
        </ToastProvider>
      </LanguageProvider>,
      reporterMessaging,
    );
  }

  const form = document.getElementById("report-form-target");
  if (form) {
    hydrate(
      <LanguageProvider language={language}>
        <ReportForm
          formId={form.dataset.formId!}
          csrfToken={form.dataset.csrfToken!}
          pageHistory={form.dataset.pageHistory!.split(",").map((p) => parseInt(p, 10))}
          pages={JSON.parse(form.dataset.pages!)}
          answers={JSON.parse(form.dataset.answers!)}
          sessionId={form.dataset.sessionId!}
          uploadWarning={null}
          csrfFailure={!!form.dataset.csrfFailure}
          ssoProvider={window.env.cognitoSsoProvider ?? null}
          isSurvey={form.dataset.isSurvey === "true"}
        />
      </LanguageProvider>,
      form,
    );
  } else {
    if (document.body.dataset.objectType && document.body.dataset.objectId) {
      sendBeacon(document.body.dataset.objectType, document.body.dataset.objectId, null, null, null);
    }
  }

  initTitleObscurer();
  wireUpFormLinks();
}

export default function main() {
  Sentry.init({
    dsn: "https://7ddb59caead6d34ba6390d9b597f7e5e@o4509785257148416.ingest.de.sentry.io/4509785307545680",
    release: window.env.sentryRelease,
    environment: window.env.sentryEnvironment,
    ignoreErrors: [
      "Failed to fetch dynamically imported module",
      "Maximum call stack size exceeded",
      "error loading dynamically imported module",
      "Failed to execute 'sendBeacon' on 'Navigator'",
      "Object captured as promise rejection with keys",
      "Only empty categories can be deleted",
      "Error invoking isAllowAutoFillOnDomain: Java object is gone",
      "swbrowser.inNightMode is not a function",
      "Non-Error promise rejection captured with value: Not implemented on this platform",
      "MediaPlaybackHandler.automaticVideoDownloadPopupEnabled is not a function",
    ],
    beforeSend(event: Sentry.Event) {
      const message = event.exception?.values?.[0]?.value ?? event.message;
      if (
        message === "$c" ||
        message === "Yc" ||
        message === "Oa" ||
        message === "fd" ||
        message === "od" ||
        message === "ud"
      ) {
        return null;
      }
      if (message === "Failed to fetch") {
        return null;
      }
      if (message === "Response not successful") {
        return null;
      }
      if (message === "Validation error") {
        return null;
      }
      if (message?.includes("Signature expired")) {
        return null;
      }
      if (message?.startsWith("Cannot read properties of undefined")) {
        return null;
      }
      if (message?.includes("Unexpected token '<'")) {
        return null;
      }
      return event;
    },
    beforeBreadcrumb: (breadcrumb: Breadcrumb) => ({
      ...breadcrumb,
      message: breadcrumb.message?.replace(uuids, "REDACTED-UUID"),
    }),
  });

  if (document.readyState === "loading") {
    window.addEventListener("DOMContentLoaded", onContentLoad);
  } else {
    onContentLoad();
  }
}
